import { useMemo } from 'react'

import { Check, CommandItem, cn } from '@hap/components'

interface CommandSelectAllProps<T extends { id: string }> {
  visibleItems: T[]
  selectedIds: string[]
  onSelect: (ids: string[]) => void
  query?: string
  multiselect?: boolean
}

export function CommandSelectAll<T extends { id: string }>({
  visibleItems,
  selectedIds,
  onSelect,
  query = '',
  multiselect,
}: CommandSelectAllProps<T>) {
  if (!multiselect || !visibleItems || visibleItems.length === 0) {
    return null
  }

  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedCount = useMemo(
    () => visibleItems.filter((item) => selectedIdsSet.has(item.id)).length,
    [visibleItems, selectedIdsSet],
  )
  const allSelected = selectedCount === visibleItems.length
  const someSelected = selectedCount > 0 && !allSelected

  const handleClick = () => {
    if (allSelected) {
      // Only deselect when ALL visible items are selected
      const visibleIdsSet = new Set(visibleItems.map((item) => item.id))
      const idsToDeselect = selectedIds.filter((id) => !visibleIdsSet.has(id))
      onSelect(idsToDeselect)
    } else {
      // Select all visible items (works for none selected AND partial)
      const visibleIds = visibleItems.map((item) => item.id)
      const newSelection = [...new Set([...selectedIds, ...visibleIds])]
      onSelect(newSelection)
    }
  }

  return (
    <CommandItem value={query} onSelect={handleClick} className="flex items-center gap-3">
      <div
        className={cn(
          'border-foreground/30 relative size-[16px] rounded-sm border-[1.25px]',
          { 'bg-foreground': allSelected },
        )}
      >
        {allSelected ? (
          <Check className="absolute top-1/2 left-1/2 -translate-1/2" />
        ) : someSelected ? (
          <div className="bg-foreground/60 absolute top-1/2 left-1/2 h-[2px] w-3 -translate-1/2" />
        ) : null}
      </div>
      <div>Select all in list ({visibleItems.length})</div>
    </CommandItem>
  )
}
